import { z } from 'zod/v4';

import { ActionPermissionEnum } from '../../backend-generated-types';
import { CustomerDTORuntype } from '../customer';
import { FeatureFlagName, FeatureFlagNameRuntype } from '../featureFlagNameBackendTypes';
import { ProductCostingAccessEnum } from '../organizationSettings';
import { ModulePermissionRuntype } from '../role';
import { UserDTORuntype } from '../user';

export enum UserType {
    Customer = 'Customer',
    Supplier = 'Supplier',
    Internal = 'Internal',
}

const ClientTypeRuntype = z.enum(['customer', 'test', 'trial', 'inactive']);

export interface OrganizationDTO extends z.infer<typeof OrganizationDTORuntype> {}
export const OrganizationDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    billing_address: z.string().nullable(),
    client_type: ClientTypeRuntype.nullable(),
    has_access_to_customer_portal: z.boolean(),
    has_full_manufacturing_access: z.boolean().optional(),
    has_customers: z.boolean(),
    is_closed_beta_tester: z.boolean(),
    has_access_to_standard_api: z.boolean().optional(),
    has_access_to_full_api: z.boolean().optional(),
});

/**
 * Identifies what authenticated the request: a human user, or a machine token
 * (e.g. SRM via the MCP server) acting on a user's behalf. Adjacently tagged
 * to mirror the backend `ActingUserActor` enum.
 */
export const ActingUserActorRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('user') }),
    z.object({
        type: z.literal('machine'),
        data: z.object({
            machine_token_id: z.string(),
            name: z.string(),
        }),
    }),
]);

export interface CurrentUserDetailsDTO extends z.infer<typeof CurrentUserDetailsDTORuntype> {}
export const CurrentUserDetailsDTORuntype = z.object({
    user: UserDTORuntype,
    tenant: z.string().min(1),
    all_tenants_for_user: z.array(z.string()),
    organization: OrganizationDTORuntype,
    customer: CustomerDTORuntype.nullable(),
    suppliers: z.array(z.unknown()),
    // Additive + optional: keeps old/new deploy-skew safe; the backend always
    // sends it, but optional avoids breaking if an older backend doesn't.
    actor: ActingUserActorRuntype.optional(),
    user_type: z.enum(['Organization', 'Customer', 'Supplier']).optional(),
});

export interface VersionInformationDTO extends z.infer<typeof VersionInformationDTORuntype> {}
export const VersionInformationDTORuntype = z.object({
    epibator_sha: z.string(),
    luminovo_version: z.string(),
});

export { FeatureFlagNameRuntype };
export type { FeatureFlagName };

export const FeatureFlagItemRuntype = z.object({
    feature_flag: z.string(),
    enabled: z.boolean(),
});
export type FeatureFlagItem = z.infer<typeof FeatureFlagItemRuntype>;

export const MyPermissionsResponseRuntype = z.object({
    module_permissions: z.array(ModulePermissionRuntype),
    action_permissions: z.array(ActionPermissionEnum),
});
export type MyPermissionsResponse = z.infer<typeof MyPermissionsResponseRuntype>;

export const ListFeatureFlagsResponseRuntype = z.object({
    data: z.array(FeatureFlagItemRuntype),
    is_advanced_role_management_enabled: z.boolean(),
    product_costing_access: ProductCostingAccessEnum,
});
export type ListFeatureFlagsResponse = z.infer<typeof ListFeatureFlagsResponseRuntype>;
